package com.essntl.components.multiselect

import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import kotlin.math.abs
import kotlin.math.min

@Composable
fun rememberMultiSelectState(selectedItemsIds: Set<String> = setOf()): MultiSelectState {
    return rememberSaveable(saver = MultiSelectState.Saver) {
        MultiSelectState(
            selectedItemsIds = selectedItemsIds,
        )
    }
}

class MultiSelectState(
    selectedItemsIds: Set<String> = setOf(),
) {
    internal val selectableItemMap = mutableMapOf<String, SelectableItemState>()

    var selectedItemsIds by mutableStateOf(selectedItemsIds)
        private set

    /**
     * Add or update [SelectableItemState] in [selectableItemMap]
     */
    fun addSelectableItem(selectableItemState: SelectableItemState) {
        selectableItemMap[selectableItemState.id] = selectableItemState
    }

    internal var containerPositionInRoot by mutableStateOf(Offset.Zero)

    var isSelecting by mutableStateOf(false)
        internal set

    internal var selectionStartOffset = Offset.Zero

    var selectionTopLeft by mutableStateOf(Offset.Zero)
        internal set

    var selectionSize by mutableStateOf(Size.Zero)
        internal set

    /**
     * @param offset - offset of the drag start position
     */
    internal fun handleDragStart(offset: Offset) {
        val dragStartOffset = offset + containerPositionInRoot

        selectionStartOffset = dragStartOffset
        selectionTopLeft = dragStartOffset
        selectionSize = Size.Zero
        isSelecting = true

        selectedItemsIds = setOf()
    }

    fun handleDrag(offset: Offset) {
        val dragOffset = offset + containerPositionInRoot

        selectionTopLeft =
            Offset(
                x = min(dragOffset.x, selectionStartOffset.x),
                y = min(dragOffset.y, selectionStartOffset.y),
            )

        selectionSize =
            Size(
                width = abs(dragOffset.x - selectionStartOffset.x),
                height = abs(dragOffset.y - selectionStartOffset.y),
            )

        val newSelectedItemsIds = mutableSetOf<String>()

        selectableItemMap.forEach { (id, selectableItemState) ->
            val selectableItemTopLeft = selectableItemState.topLeft
            val selectableItemSize = selectableItemState.size

            if (
                isRectangleIntersected(
                    topLeft1 = selectionTopLeft,
                    size1 = selectionSize,
                    topLeft2 = selectableItemTopLeft,
                    size2 = selectableItemSize,
                )
            ) {
                newSelectedItemsIds.add(id)
            }
        }

        selectedItemsIds = newSelectedItemsIds
    }

    fun handleDrop() {
        selectionStartOffset = Offset.Zero
        selectionTopLeft = Offset.Zero
        selectionSize = Size.Zero
        isSelecting = false
    }

    companion object {
        internal val Saver =
            Saver<MultiSelectState, Set<String>>(
                save = { multiSelectState ->
                    multiSelectState.selectedItemsIds
                },
                restore = { values ->
                    MultiSelectState(
                        selectedItemsIds = values,
                    )
                },
            )
    }
}

private fun isRectangleIntersected(
    topLeft1: Offset,
    size1: Size,
    topLeft2: Offset,
    size2: Size,
): Boolean {
    val right1 = topLeft1.x + size1.width
    val bottom1 = topLeft1.y + size1.height

    val right2 = topLeft2.x + size2.width
    val bottom2 = topLeft2.y + size2.height

    return !(right1 < topLeft2.x || right2 < topLeft1.x || bottom1 < topLeft2.y || bottom2 < topLeft1.y)
}